import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { isExtraBlockRenderable, loadThemeExtraBlocks, type ThemeExtraBlock } from '@/lib/themeExtraBlocks';

// ─────────────────────────────────────────────────────────────
// Rendu public des blocs « en plus » du thème. Chaque champ est
// facultatif : un champ vide ou masqué n'est simplement pas rendu.
// Les images sont affichées en `object-top` dans un cadre explicite.
// ─────────────────────────────────────────────────────────────

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function BlockButton({ label, href }: { label: string; href: string }) {
  const className =
    'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap no-underline hover:bg-primary-600 transition-colors cursor-pointer';
  if (href && isExternal(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {label}
        <i className="ri-arrow-right-up-line"></i>
      </a>
    );
  }
  if (href) {
    return (
      <Link to={href} className={className}>
        {label}
        <i className="ri-arrow-right-line"></i>
      </Link>
    );
  }
  return null;
}

function ExtraBlock({ block }: { block: ThemeExtraBlock }) {
  const showTitle = !block.hideTitle && block.title.trim() !== '';
  const showDescription = !block.hideDescription && block.description.trim() !== '';
  const showButton = !block.hideButton && block.buttonLabel.trim() !== '';
  const showImage = !block.hideImage && block.image.trim() !== '';
  const hasBackground = block.backgroundImage.trim() !== '';
  const isCenter = block.layout === 'center' || block.layout === 'full';

  const textBlock = (
    <div className={`min-w-0 ${isCenter ? 'text-center max-w-2xl mx-auto' : ''}`}>
      {showTitle && (
        <h2 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
          {block.title}
        </h2>
      )}
      {showDescription && (
        <p className={`text-sm md:text-base text-foreground-600 leading-relaxed whitespace-pre-line ${showTitle ? 'mt-3' : ''}`}>
          {block.description}
        </p>
      )}
      {showButton && (
        <div className={`${showTitle || showDescription ? 'mt-5' : ''} flex ${isCenter ? 'justify-center' : 'justify-start'}`}>
          <BlockButton label={block.buttonLabel} href={block.buttonHref} />
        </div>
      )}
    </div>
  );

  return (
    <section className="w-full px-4 md:px-6 py-9 md:py-12">
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl border border-background-200/70 bg-background-50">
          {hasBackground && (
            <>
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${block.backgroundImage})` }}
                aria-hidden="true"
              ></div>
              <div className="absolute inset-0 bg-background-50/85" aria-hidden="true"></div>
            </>
          )}

          <div className="relative p-5 md:p-8">
            {isCenter ? (
              <div className="flex flex-col gap-5">
                {textBlock}
                {showImage && (
                  <div className="w-full h-48 md:h-72 rounded-xl overflow-hidden bg-background-100">
                    <img
                      src={block.image}
                      alt={block.title || 'Illustration'}
                      title={block.title || 'Illustration'}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div
                className={`flex flex-col gap-5 md:gap-8 md:items-center ${
                  block.layout === 'image-right' ? 'md:flex-row-reverse' : 'md:flex-row'
                }`}
              >
                {showImage && (
                  <div className="w-full md:w-[46%] h-44 md:h-64 rounded-xl overflow-hidden bg-background-100 flex-shrink-0">
                    <img
                      src={block.image}
                      alt={block.title || 'Illustration'}
                      title={block.title || 'Illustration'}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">{textBlock}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Affiche les blocs supplémentaires configurés par le marchand.
 * Ne rend rien s'il n'y a aucun bloc visible : aucun espace vide.
 */
export default function ThemeExtraBlocksRenderer({ className = '' }: { className?: string }) {
  const { tenant, isTenant } = useTenant();
  const merchantId = tenant?.id ?? null;
  const [blocks, setBlocks] = useState<ThemeExtraBlock[]>([]);

  useEffect(() => {
    if (!isTenant || !merchantId) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await loadThemeExtraBlocks(Number(merchantId));
        if (!cancelled) setBlocks(rows);
      } catch {
        if (!cancelled) setBlocks([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isTenant, merchantId]);

  const visible = blocks.filter(isExtraBlockRenderable);
  if (visible.length === 0) return null;

  return (
    <div className={`w-full ${className}`}>
      {visible.map((block) => (
        <ExtraBlock key={block.id} block={block} />
      ))}
    </div>
  );
}